import { Badge } from "@/components/ui/badge"
import { cn } from "@/lib/utils"
import { formatMoney } from "./money"
import type { MenuItem } from "./types"
import { t } from "@/lib/i18n"

/**
 * What a dish costs, with the standing rule already in it.
 *
 * ## Why this is one component and not three copies
 *
 * The promoted price has to reach the diner in every place a price is shown
 * *before they commit* — the menu row, the featured carousel, the detail sheet.
 * A rule the diner only meets on the bill is not a promotion, it is a surprise,
 * and the restaurant paid for the opposite. Three hand-kept copies of "strike
 * the old one, show the new one" drift, and the one that drifts is the one
 * nobody looks at.
 *
 * ## The design rules this obeys, and why they are not decoration
 *
 * The dot rule: colour lives in small marks, never in large text. A promoted
 * price is precisely where somebody reaches for a big green number, so the
 * figure stays `text-foreground font-semibold tabular-nums` — identical to an
 * unpromoted price — and the *contrast* does the work: the original beside it,
 * struck through and muted. The rule's name rides in a `Badge variant="secondary"`,
 * the same neutral mark `FeaturedCard` already uses for "Recomendado", so a
 * promotion introduces no new visual vocabulary.
 *
 * Reading order is original then promoted, so the last figure — the heaviest
 * one — is the one they pay.
 */
export function DishPrice({
  item,
  className,
}: {
  item: Pick<MenuItem, "id" | "price" | "currency" | "promotion">
  className?: string
}) {
  const promotion = item.promotion ?? null

  if (!promotion) {
    return (
      <span
        className={cn("text-sm font-semibold tabular-nums text-foreground", className)}
        data-testid={`dish-price-${item.id}`}
      >
        {formatMoney(item.price, { currency: item.currency })}
      </span>
    )
  }

  return (
    <span
      className={cn("inline-flex items-baseline gap-1.5", className)}
      data-testid={`dish-price-${item.id}`}
      data-promoted="true"
    >
      {/* Announced, not just struck: a line-through is a visual convention a
          screen reader does not speak, and "$120 $96" read aloud with no
          qualifier is a price nobody can act on. */}
      <span className="text-xs tabular-nums text-muted-foreground line-through">
        <span className="sr-only">{t("ui.react.features.user.dish_price.original")} </span>
        {formatMoney(item.price, { currency: item.currency })}
      </span>
      <span
        className="text-sm font-semibold tabular-nums text-foreground"
        data-testid={`dish-promoted-price-${item.id}`}
      >
        <span className="sr-only">{t("ui.react.features.user.dish_price.promoted")} </span>
        {formatMoney(promotion.promotedPrice, { currency: item.currency })}
      </span>
    </span>
  )
}

/**
 * The rule's own name — "Happy hour", "Martes de tacos" — as the restaurant
 * wrote it.
 *
 * Staff-authored, so it is never translated: an i18n key here would replace the
 * thing the diner was actually promised with a generic word for it.
 */
export function DishPromotionBadge({
  item,
  className,
}: {
  item: Pick<MenuItem, "id" | "promotion">
  className?: string
}) {
  const promotion = item.promotion ?? null
  if (!promotion) return null

  return (
    <Badge
      variant="secondary"
      className={cn("max-w-full truncate", className)}
      data-testid={`dish-promotion-${item.id}`}
    >
      {promotion.name}
    </Badge>
  )
}
